"use client";

import { c as _c } from "react/compiler-runtime";
import { useCarePlanContext } from "../care-plan";
import { findStep } from "../types";
import { createContext, use, useCallback, useMemo, useState } from "react";
import createId from "@/lib/create-id";
import type { SequenceDraft, SequencesContextValue, SequencesMeta } from "./types";

/** New sequences default to firing 2 days before the appointment time. */
const DEFAULT_DELAY_MINUTES = -2880;
export const SequencesContext = createContext<SequencesContextValue | null>(null);
export function useSequences(): SequencesContextValue {
  const ctx = use(SequencesContext);
  if (!ctx) {
    throw new Error("useSequences must be used within a <StepSequencesProvider>");
  }
  return ctx;
}
interface StepSequencesProviderProps {
  stepId: string;
  readOnly?: boolean;
  children: React.ReactNode;
}

/**
 * Reads `step.sequences` out of the surrounding care-plan editor state and
 * writes back via `actions.updateStep`. The provider does not own the array —
 * it derives the draft list and forwards mutations.
 */
export function StepSequencesProvider(t0) {
  const $ = _c(36);
  const {
    stepId,
    readOnly: readOnlyProp,
    children
  } = t0;
  const {
    state: t1,
    actions: t2,
    config: t3,
    lookups: t4
  } = useCarePlanContext();
  const {
    steps
  } = t1;
  const {
    updateStep
  } = t2;
  const {
    readOnly: planReadOnly
  } = t3;
  const {
    patientId
  } = t4;
  let t5;
  if ($[0] !== stepId || $[1] !== steps) {
    t5 = findStep(steps, stepId);
    $[0] = stepId;
    $[1] = steps;
    $[2] = t5;
  } else {
    t5 = $[2];
  }
  const step = t5;
  let t6;
  if ($[3] !== step?.sequences) {
    t6 = step?.sequences ?? [];
    $[3] = step?.sequences;
    $[4] = t6;
  } else {
    t6 = $[4];
  }
  const drafts = t6;
  const [editingKey, setEditingKey] = useState(null);
  const [pendingDraft, setPendingDraft] = useState(null);
  let t7;
  if ($[5] !== stepId || $[6] !== updateStep) {
    t7 = next => {
      updateStep(stepId, {
        sequences: next,
        sequencesDirty: true
      });
    };
    $[5] = stepId;
    $[6] = updateStep;
    $[7] = t7;
  } else {
    t7 = $[7];
  }
  const writeDrafts = t7;
  let t8;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = () => {
      const key = createId("seq");
      const next_0 = {
        key,
        delayMinutes: DEFAULT_DELAY_MINUTES,
        effectType: "SEND_SMS",
        smsBody: "",
        isActive: true
      };
      setPendingDraft(next_0);
      return key;
    };
    $[8] = t8;
  } else {
    t8 = $[8];
  }
  const add = t8;
  let t9;
  if ($[9] !== drafts || $[10] !== pendingDraft || $[11] !== writeDrafts) {
    t9 = (key_0, updates) => {
      if (pendingDraft && pendingDraft.key === key_0) {
        const next_1 = {
          ...pendingDraft,
          ...updates
        };
        if (draftHasContent(next_1)) {
          setPendingDraft(null);
          writeDrafts([...drafts, next_1]);
        } else {
          setPendingDraft(next_1);
        }
        return;
      }
      writeDrafts(drafts.map(d => d.key === key_0 ? {
        ...d,
        ...updates
      } : d));
    };
    $[9] = drafts;
    $[10] = pendingDraft;
    $[11] = writeDrafts;
    $[12] = t9;
  } else {
    t9 = $[12];
  }
  const update = t9;
  let t10;
  if ($[13] !== drafts || $[14] !== pendingDraft || $[15] !== writeDrafts) {
    t10 = key_1 => {
      if (pendingDraft && pendingDraft.key === key_1) {
        setPendingDraft(null);
        return;
      }
      writeDrafts(drafts.filter(d_0 => d_0.key !== key_1));
    };
    $[13] = drafts;
    $[14] = pendingDraft;
    $[15] = writeDrafts;
    $[16] = t10;
  } else {
    t10 = $[16];
  }
  const remove = t10;
  let t11;
  if ($[17] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = () => {
      setPendingDraft(null);
    };
    $[17] = t11;
  } else {
    t11 = $[17];
  }
  const discardPending = t11;
  const t12 = !!readOnlyProp || planReadOnly;
  let t13;
  if ($[18] !== patientId || $[19] !== stepId || $[20] !== t12) {
    t13 = {
      stepId,
      readOnly: t12,
      patientId
    };
    $[18] = patientId;
    $[19] = stepId;
    $[20] = t12;
    $[21] = t13;
  } else {
    t13 = $[21];
  }
  const meta = t13;
  let t14;
  if ($[22] !== drafts || $[23] !== editingKey || $[24] !== pendingDraft) {
    t14 = {
      drafts,
      pendingDraft,
      editingKey
    };
    $[22] = drafts;
    $[23] = editingKey;
    $[24] = pendingDraft;
    $[25] = t14;
  } else {
    t14 = $[25];
  }
  let t15;
  if ($[26] !== remove || $[27] !== update) {
    t15 = {
      add,
      update,
      remove,
      discardPending,
      setEditingKey
    };
    $[26] = remove;
    $[27] = update;
    $[28] = t15;
  } else {
    t15 = $[28];
  }
  let t16;
  if ($[29] !== meta || $[30] !== t14 || $[31] !== t15) {
    t16 = {
      state: t14,
      actions: t15,
      meta
    };
    $[29] = meta;
    $[30] = t14;
    $[31] = t15;
    $[32] = t16;
  } else {
    t16 = $[32];
  }
  const value = t16;
  let t17;
  if ($[33] !== children || $[34] !== value) {
    t17 = <SequencesContext value={value}>{children}</SequencesContext>;
    $[33] = children;
    $[34] = value;
    $[35] = t17;
  } else {
    t17 = $[35];
  }
  return t17;
}
function draftHasContent(d: SequenceDraft): boolean {
  return !!(stripWhitespace(d.smsBody) || stripWhitespace(d.emailSubject) || stripWhitespace(d.emailBody));
}
function stripWhitespace(value: string | undefined): string {
  return (value ?? "").replace(/<[^>]*>/g, "").trim();
}